import React, { useState, useMemo } from 'react';
import { Card, SectionHeader, Btn, Badge, Input, Select, EmptyState } from './UI';
import { Plus, Trash2, ChevronLeft, ChevronRight, AlertCircle, RotateCcw } from 'lucide-react';
import { nowMonth, getMonthLabel, fmtDate, today } from '../utils/helpers';
import { autoStatusFor, awaitingItems } from '../utils/autoStatus';

// Monthly-work tracker — a tab inside Creative Studio (rendered in both the admin shell
// and the employee portal). Modelled on the Strategists Monthly Content Planner: cycles
// are named by their GO-LIVE month, work runs the month before on a fixed timeline, and
// each brand seeds its own deliverables + volumes. One row per deliverable (safe
// concurrent edits). Writes go through the standard persist()/saveStore path.

const STATUSES = ['Not started', 'In progress', 'Sent for review', 'Changes requested', 'Approved', 'Done'];
const STATUS_DOT = {
  'Not started': 'var(--text3)', 'In progress': 'var(--blue)', 'Sent for review': 'var(--purple)',
  'Changes requested': 'var(--red)', 'Approved': 'var(--accent)', 'Done': 'var(--green)',
};
const DONE = s => s === 'Done' || s === 'Approved';
// Manual-status → Badge color, and computed-tone → seed manual status when an admin
// clicks "Set manually" to override an Auto row.
const MANUAL_COLOR = {
  'Not started': 'gray', 'In progress': 'blue', 'Sent for review': 'purple',
  'Changes requested': 'red', 'Approved': 'green', 'Done': 'green',
};
const SEED_OVERRIDE = { green: 'Done', red: 'Changes requested', purple: 'Sent for review', orange: 'In progress', blue: 'In progress', gray: 'Not started' };
const TONE_VAR = { gray: 'var(--text3)', blue: 'var(--blue)', green: 'var(--green)', red: 'var(--red)', orange: 'var(--orange)', purple: 'var(--purple)', teal: 'var(--accent)' };
// Deliverables group into phases of the content month, for a scannable layout.
const PHASE = {
  planning: 'Creative', design: 'Creative', mockup: 'Creative', stories: 'Creative',
  schedule: 'Distribution', ads: 'Distribution', banners: 'Distribution', pinterest: 'Distribution', newsletter: 'Distribution', community: 'Distribution',
  report: 'Reporting',
};
const PHASE_ORDER = ['Creative', 'Distribution', 'Reporting', 'Other'];

// ── Cycle timeline: due date for each deliverable, relative to the live month ──
function ymParts(ym) { const [y, m] = ym.split('-').map(Number); return { y, m }; }
function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function dateFor(ym, offMonths, day) { const { y, m } = ymParts(ym); return iso(new Date(y, (m - 1) + offMonths, day)); }
function lastDayFor(ym, offMonths) { const { y, m } = ymParts(ym); return iso(new Date(y, (m - 1) + offMonths + 1, 0)); }

const CYCLE = {
  planning:   { label: 'Content calendar — planning',            auto: true, due: ym => dateFor(ym, -1, 12) },
  design:     { label: 'Content calendar — design (posts & reels)', auto: true, due: ym => dateFor(ym, -1, 20) },
  mockup:     { label: 'Monthly mockup',                          auto: true, due: ym => dateFor(ym, -1, 22) },
  ads:        { label: 'Ad campaign management',                              due: ym => dateFor(ym, -1, 28) },
  schedule:   { label: 'Scheduling — posts, reels & stories loaded',          due: ym => dateFor(ym, -1, 29) },
  stories:    { label: 'Stories',                                             due: ym => dateFor(ym, -1, 29) },
  banners:    { label: 'Banners / covers',                                    due: ym => lastDayFor(ym, -1) },
  community:  { label: 'Community management',                                due: ym => lastDayFor(ym, 0) },
  pinterest:  { label: 'Pinterest pins',                                      due: ym => dateFor(ym, -1, 29) },
  newsletter: { label: 'Newsletter',                                          due: ym => dateFor(ym, 0, 12) },
  report:     { label: 'Monthly performance report',                          due: ym => dateFor(ym, 1, 5) },
};

// ── Per-brand plans (from the Monthly Content Planner). inScope:false = seeded but off. ──
const BRAND_PLANS = {
  'vitraguard': [
    { k: 'planning' }, { k: 'design', range: '5–6 rows' }, { k: 'mockup' },
    { k: 'stories', perDay: '1 / 2–3 days' }, { k: 'schedule' }, { k: 'report' },
  ],
  'padel edge': [
    { k: 'planning' }, { k: 'design', range: '3–4 rows' }, { k: 'stories', perDay: '2 / day' },
    { k: 'schedule' }, { k: 'ads', range: 'On request', inScope: false }, { k: 'community' }, { k: 'report' },
  ],
  'rashid fabrics': [
    { k: 'planning' }, { k: 'design', range: '4–6 rows' }, { k: 'mockup' },
    { k: 'ads', range: '2 static + 2 video' }, { k: 'schedule' },
    { k: 'banners', range: 'FB + web cover (bi-monthly)', inScope: false },
    { k: 'stories', perDay: 'non-post days' }, { k: 'community' }, { k: 'report' },
  ],
  'signature aesthetics & wellness': [
    { k: 'planning' }, { k: 'design', range: '7–8 rows' }, { k: 'mockup' },
    { k: 'ads', range: 'Deal + Traffic' }, { k: 'schedule' }, { k: 'banners', range: 'Monthly banner' }, { k: 'report' },
  ],
  'signature by eram': [
    { k: 'planning' }, { k: 'design', range: '8–10 rows' }, { k: 'mockup' },
    { k: 'ads', range: 'Deal + Traffic' }, { k: 'schedule' }, { k: 'banners', range: 'Monthly banner' }, { k: 'report' },
  ],
  'haaf group': [
    { k: 'planning' }, { k: 'design', range: '4–6 rows' }, { k: 'mockup' }, { k: 'schedule' },
  ],
  'isov': [
    { k: 'planning' }, { k: 'design', range: '4–5 rows' }, { k: 'mockup' },
    { k: 'ads', range: 'Engagement + Traffic' }, { k: 'schedule' }, { k: 'banners', range: 'Monthly banner' }, { k: 'report' },
  ],
  'amina khalid': [
    { k: 'planning' }, { k: 'design', range: '9–10 rows' }, { k: 'mockup' },
    { k: 'ads', range: '4–5 ads' }, { k: 'schedule' }, { k: 'banners', range: 'Monthly banner' },
    { k: 'stories', perDay: '2 / day' }, { k: 'report' },
  ],
  'hcw home': [
    { k: 'planning' }, { k: 'design', range: '4–6 rows · 3–5 posts/wk' }, { k: 'mockup' },
    { k: 'schedule' }, { k: 'pinterest' }, { k: 'newsletter' }, { k: 'community' }, { k: 'report' },
  ],
  'dhaanak by mahnoor': [
    { k: 'planning' }, { k: 'design', range: '10–11 rows' }, { k: 'mockup' },
    { k: 'ads', range: '4–5 ads' }, { k: 'schedule' }, { k: 'stories', perDay: '3 / day' }, { k: 'community' },
  ],
};
const GENERIC_PLAN = [
  { k: 'planning' }, { k: 'design' }, { k: 'stories' }, { k: 'mockup' },
  { k: 'ads', inScope: false }, { k: 'schedule' }, { k: 'community', inScope: false }, { k: 'report' },
];
const norm = s => (s || '').trim().toLowerCase();

function uid() { return 'mw_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }
function shiftMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shortDate(ymd) { if (!ymd) return ''; const d = new Date(ymd); return `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`; }
function plusDays(ymd, n) { const d = new Date(ymd); d.setDate(d.getDate() + n); return iso(d); }

// Circular progress ring with the percentage in the middle.
function ProgressRing({ pct }) {
  const r = 22, c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, pct));
  const off = c * (1 - p / 100);
  const stroke = p === 100 ? 'var(--green)' : 'var(--accent)';
  return (
    <svg width="58" height="58" viewBox="0 0 56 56" style={{ flexShrink: 0 }}>
      <circle cx="28" cy="28" r={r} fill="none" stroke="var(--bg4)" strokeWidth="6" />
      <circle cx="28" cy="28" r={r} fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 28 28)"
        style={{ transition: 'stroke-dashoffset .55s cubic-bezier(.2,.8,.2,1), stroke .3s' }} />
      <text x="28" y="29" textAnchor="middle" dominantBaseline="middle" style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: '13.5px', fontWeight: 700, fill: 'var(--text)' }}>{p}%</text>
    </svg>
  );
}

export default function MonthlyWorkTracker({ store, persist, session }) {
  const isAdmin = session?.role === 'admin' || session?.empId === 'admin';
  const empId = session?.empId;
  const employees = store.employees || [];
  const clients = useMemo(() => (store.clients || []).filter(c => !c.formerClient), [store.clients]);
  const all = store.monthlyWork || [];

  const [month, setMonth] = useState(nowMonth());
  const [clientId, setClientId] = useState('');
  const activeClient = clientId || clients[0]?.id || '';
  const activeClientObj = clients.find(c => c.id === activeClient);
  const hasPlan = activeClientObj && !!BRAND_PLANS[norm(activeClientObj.name)];

  const items = useMemo(() => {
    if (isAdmin) {
      return all.filter(w => w.clientId === activeClient && w.monthYear === month)
                .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    }
    return all.filter(w => w.ownerId === empId && w.monthYear === month && w.inScope !== false)
              .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
  }, [all, activeClient, month, isAdmin, empId]);

  const clientName = id => clients.find(c => c.id === id)?.name || '—';
  // A row counts as done by its manual status, its admin override, or (for an Auto row
  // with no override) the computed status being green (client-approved / planned).
  const effectiveDone = (it) => {
    if (it.auto && it.cycleKey) {
      if (it.statusOverride) return DONE(it.statusOverride);
      const s = autoStatusFor(it.cycleKey, store, it.clientId, it.monthYear);
      return s ? s.tone === 'green' : false;
    }
    return DONE(it.status);
  };
  const doneCount = items.filter(i => i.inScope !== false && effectiveDone(i)).length;
  const scopeCount = items.filter(i => i.inScope !== false).length;

  // Colored status dot for a row (manual status, admin override, or computed tone).
  const rowDot = (it) => {
    if (it.auto && it.cycleKey) {
      if (it.statusOverride) return STATUS_DOT[it.statusOverride] || 'var(--text3)';
      const s = autoStatusFor(it.cycleKey, store, it.clientId, it.monthYear);
      return (s && TONE_VAR[s.tone]) || 'var(--text3)';
    }
    return STATUS_DOT[it.status] || 'var(--text3)';
  };
  // Group the visible rows into content phases (empty phases hidden).
  const grouped = PHASE_ORDER.map(ph => {
    const rows = items.filter(it => (PHASE[it.cycleKey] || 'Other') === ph);
    const scope = rows.filter(r => r.inScope !== false).length;
    const done = rows.filter(r => r.inScope !== false && effectiveDone(r)).length;
    return { ph, rows, scope, done };
  }).filter(g => g.rows.length);

  // Home nudge: items the current user owns that are overdue or due within 10 days.
  const myOpen = useMemo(() => {
    const soon = plusDays(today(), 10);
    return all.filter(w => w.ownerId === empId && w.inScope !== false && !DONE(w.status) && w.dueDate && w.dueDate <= soon)
              .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
  }, [all, empId]);

  // Awaiting-client-content bucket for the active client + cycle (admin view).
  const awaiting = useMemo(
    () => (isAdmin && activeClient) ? awaitingItems(store, activeClient, month) : [],
    [store, activeClient, month, isAdmin]
  );

  // Milestone markers for the selected cycle (approval + go-live shown here, not as rows).
  // "Ads" only appears if this client actually runs ads — an in-scope ads deliverable this
  // cycle, or (before the cycle is set up) an ad type on the client's profile.
  const clientHasAds = items.length
    ? items.some(it => it.cycleKey === 'ads' && it.inScope !== false)
    : !!(activeClientObj && activeClientObj.adType);
  const milestones = [
    ['Plan', dateFor(month, -1, 12)], ['Design', dateFor(month, -1, 20)], ['Mockup', dateFor(month, -1, 22)],
    ['Approvals', dateFor(month, -1, 25)],
    ...(clientHasAds ? [['Ads', dateFor(month, -1, 28)]] : []),
    ['Scheduled', dateFor(month, -1, 29)],
    ['Live', dateFor(month, 0, 1)], ['Report', dateFor(month, 1, 5)],
  ];

  function patch(id, p) { persist({ ...store, monthlyWork: all.map(w => w.id === id ? { ...w, ...p } : w) }); }
  function remove(id) { persist({ ...store, monthlyWork: all.filter(w => w.id !== id) }, { deletedIds: { monthlyWork: [id] } }); }
  function addRow() {
    const row = {
      id: uid(), clientId: activeClient, monthYear: month, type: '', cycleKey: '',
      inScope: true, monthlyRange: '', perDay: '', ownerId: '', dueDate: '',
      status: 'Not started', link: '', notes: '', auto: false,
      sortOrder: items.length, createdAt: today(), createdBy: empId,
    };
    persist({ ...store, monthlyWork: [...all, row] });
  }
  function seedStandard() {
    const plan = (activeClientObj && BRAND_PLANS[norm(activeClientObj.name)]) || GENERIC_PLAN;
    const existing = new Set(items.map(i => i.type));
    const rows = plan.filter(p => !existing.has(CYCLE[p.k].label)).map((p, i) => {
      const def = CYCLE[p.k];
      return {
        id: uid(), clientId: activeClient, monthYear: month, type: def.label, cycleKey: p.k,
        inScope: p.inScope !== false, monthlyRange: p.range || '', perDay: p.perDay || '',
        ownerId: '', dueDate: def.due(month), status: 'Not started', link: '', notes: '',
        auto: !!def.auto, sortOrder: items.length + i, createdAt: today(), createdBy: empId,
      };
    });
    if (rows.length) persist({ ...store, monthlyWork: [...all, ...rows] });
  }
  function copyLastMonth() {
    const prev = shiftMonth(month, -1);
    const prevItems = all.filter(w => w.clientId === activeClient && w.monthYear === prev);
    const existing = new Set(items.map(i => i.type));
    const rows = prevItems.filter(p => !existing.has(p.type)).map((p, i) => ({
      ...p, id: uid(), monthYear: month, status: 'Not started', link: '', notes: '',
      // roll the due date forward one cycle for known deliverables
      dueDate: p.cycleKey && CYCLE[p.cycleKey] ? CYCLE[p.cycleKey].due(month) : p.dueDate,
      sortOrder: items.length + i, createdAt: today(), createdBy: empId,
    }));
    if (rows.length) persist({ ...store, monthlyWork: [...all, ...rows] });
  }

  const th = { textAlign: 'left', padding: '11px 14px', fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const td = { padding: '10px 14px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle', fontSize: 13, color: 'var(--text)' };
  const mini = { padding: '6px 8px', fontSize: 12.5 };

  const MonthStepper = () => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: 2, background: 'var(--bg2)' }}>
      <button onClick={() => setMonth(m => shiftMonth(m, -1))} title="Previous cycle" style={{ background: 'none', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', padding: 5, display: 'flex', color: 'var(--text2)' }}><ChevronLeft size={16} /></button>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', minWidth: 138, textAlign: 'center' }}>{getMonthLabel(month)} cycle</span>
      <button onClick={() => setMonth(m => shiftMonth(m, 1))} title="Next cycle" style={{ background: 'none', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer', padding: 5, display: 'flex', color: 'var(--text2)' }}><ChevronRight size={16} /></button>
    </div>
  );

  const StatusSelect = ({ it }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[it.status] || 'var(--text3)', flexShrink: 0 }} />
      <Select value={it.status || 'Not started'} onChange={e => patch(it.id, { status: e.target.value })} style={{ minWidth: 140, ...mini }}>
        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
      </Select>
    </span>
  );

  // Auto rows (planning / design / mockup) show a status computed live from the client's
  // calendar & mockup. It's read-only by default — but an admin can override it manually
  // (e.g. work done before the app, or an already-built calendar). An override is clearly
  // labelled and one click reverts to automatic.
  const progress = (s) => s.total > 0
    ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}
        title={`${s.done} of ${s.total} done${s.awaiting ? ` · ${s.awaiting} awaiting client` : ''}`}>
        <span style={{ width: 56, height: 5, borderRadius: 'var(--radius)', background: 'var(--bg4)', overflow: 'hidden', display: 'inline-block', flexShrink: 0 }}>
          <span className="bar-grow" style={{ display: 'block', height: '100%', borderRadius: 'var(--radius)', width: `${Math.round(s.done / s.total * 100)}%`, background: s.done === s.total ? 'var(--green)' : 'var(--accent)', transition: 'width .45s cubic-bezier(.2,.8,.2,1)' }} />
        </span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--text3)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{s.done}/{s.total}{s.awaiting ? ` · ${s.awaiting} waiting` : ''}</span>
      </span>
    )
    : null;
  const StatusCell = ({ it }) => {
    if (it.auto && it.cycleKey) {
      const s = autoStatusFor(it.cycleKey, store, it.clientId, it.monthYear) || { label: 'Not started', tone: 'gray', total: 0 };
      const ov = it.statusOverride;
      if (isAdmin) {
        if (ov) return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} title="Manually set — click Auto to go back to automatic">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[ov] || 'var(--text3)', flexShrink: 0 }} />
            <Select value={ov} onChange={e => patch(it.id, { statusOverride: e.target.value })} style={{ minWidth: 130, ...mini }}>
              {STATUSES.map(x => <option key={x} value={x}>{x}</option>)}
            </Select>
            <button title="Back to automatic" onClick={() => patch(it.id, { statusOverride: '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 11.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}><RotateCcw size={12} />Auto</button>
          </span>
        );
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Badge label={s.label} color={s.tone} />
            {progress(s)}
            <button title="Set the status manually (overrides the automatic one)" onClick={() => patch(it.id, { statusOverride: SEED_OVERRIDE[s.tone] || 'Done' })} style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text3)', fontSize: 11, fontWeight: 600, padding: '3px 8px' }}>Set manually</button>
          </span>
        );
      }
      // non-admin: read-only (override if an admin set one, else the computed status)
      if (ov) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Badge label={ov} color={MANUAL_COLOR[ov] || 'gray'} /></span>;
      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} title="Set automatically from the calendar & mockup"><Badge label={s.label} color={s.tone} />{progress(s)}</span>;
    }
    return <StatusSelect it={it} />;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 className="t-h3" style={{ margin: 0 }}>Monthly Work</h2>
          <p className="t-sub" style={{ marginTop: 4 }}>
            {isAdmin ? 'Each brand’s recurring deliverables, on the monthly content timeline.' : 'Your deliverables for the cycle — mark what you’ve done.'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {isAdmin && (
            <Select value={activeClient} onChange={e => setClientId(e.target.value)} style={{ minWidth: 200, ...mini }}>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          )}
          <MonthStepper />
        </div>
      </div>

      {/* Cycle timeline (admin context) */}
      {isAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', padding: '10px 14px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', rowGap: 8 }}>
          {milestones.map(([label, date], i) => (
            <React.Fragment key={label}>
              <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 62 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: label === 'Live' ? 'var(--accent)' : 'var(--text3)' }}>{label}</span>
                <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 12.5, fontWeight: 600, color: 'var(--text2)' }}>{shortDate(date)}</span>
              </span>
              {i < milestones.length - 1 && <span style={{ flex: '0 0 18px', height: 1, background: 'var(--border2)', margin: '0 2px', alignSelf: 'center' }} />}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Home nudge */}
      {myOpen.length > 0 && (
        <Card style={{ borderColor: 'rgba(199,154,91,0.35)' }}>
          <button onClick={() => setMonth(myOpen[0].monthYear)} style={{
            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 11,
            padding: '13px 18px', border: 'none', background: 'var(--orange-bg)', borderRadius: 'var(--radius-lg)',
            cursor: 'pointer', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13,
          }}>
            <AlertCircle size={17} style={{ color: 'var(--orange)', flexShrink: 0 }} />
            <span>You have <strong>{myOpen.length}</strong> {myOpen.length === 1 ? 'deliverable' : 'deliverables'} due soon or overdue — tap to review and mark what you’ve done.</span>
          </button>
        </Card>
      )}

      {/* Main */}
      <Card>
        <SectionHeader
          title={isAdmin ? (clientName(activeClient) || 'Client') : `${getMonthLabel(month)} — my deliverables`}
          action={isAdmin && items.length > 0 ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn size="sm" variant="ghost" onClick={copyLastMonth}>Copy last cycle</Btn>
              <Btn size="sm" variant="default" onClick={addRow}><Plus size={14} /> Add row</Btn>
            </div>
          ) : undefined}
        />

        {items.length === 0 ? (
          <div style={{ padding: '30px 22px', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
            <EmptyState message={isAdmin ? 'No deliverables set for this brand this cycle yet.' : 'Nothing assigned to you this cycle yet.'} />
            {isAdmin && activeClient && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <Btn variant="primary" onClick={seedStandard}><Plus size={15} /> {hasPlan ? `Set up ${clientName(activeClient)}` : 'Add standard set'}</Btn>
                <Btn variant="default" onClick={copyLastMonth}>Copy last cycle</Btn>
                {hasPlan && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Pre-fills this brand’s deliverables, volumes & due dates.</span>}
              </div>
            )}
          </div>
        ) : (
          <div>
            {scopeCount > 0 && (
              <div style={{ margin: '4px 22px 4px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 16, background: 'linear-gradient(180deg, var(--bg2), var(--bg))', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                <ProgressRing pct={Math.round(doneCount / scopeCount * 100)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>
                    {doneCount === scopeCount ? 'All deliverables complete' : `${doneCount} of ${scopeCount} deliverables done`}
                  </div>
                  <div style={{ marginTop: 3, fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span>{scopeCount - doneCount === 0 ? 'Nothing left this cycle' : `${scopeCount - doneCount} remaining`}</span>
                    {isAdmin && awaiting.length > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--orange, #C79A5B)', fontWeight: 600 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--orange, #C79A5B)' }} />
                        {awaiting.length} awaiting client
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
            {grouped.map(g => (
              <div key={g.ph}>
                <div style={{ padding: '12px 22px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>{g.ph}</span>
                  <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  {g.scope > 0 && <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 11, fontWeight: 600, color: g.done === g.scope ? 'var(--green)' : 'var(--text3)' }}>{g.done}/{g.scope}</span>}
                </div>
                {g.rows.map(it => isAdmin ? (
                  <div key={it.id} style={{ padding: '11px 22px', borderTop: '1px solid var(--border)', opacity: it.inScope === false ? 0.5 : 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: rowDot(it), flexShrink: 0 }} />
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0 }}>
                        {it.type || 'New deliverable'}
                        {it.auto && <span style={{ marginLeft: 8 }}><Badge label="Auto" color="teal" /></span>}
                      </span>
                      <StatusCell it={it} />
                      <button title="Delete" onClick={() => remove(it.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4, display: 'flex', flexShrink: 0 }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; }} onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', paddingLeft: 19 }}>
                      <button onClick={() => patch(it.id, { inScope: !(it.inScope !== false) })}
                        style={{ background: 'none', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '4px 11px', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: it.inScope !== false ? 'var(--green)' : 'var(--text3)' }}>
                        {it.inScope !== false ? 'In scope' : 'Out of scope'}
                      </button>
                      <Select value={it.ownerId || ''} onChange={e => patch(it.id, { ownerId: e.target.value })} style={{ minWidth: 130, ...mini }}>
                        <option value="">— unassigned —</option>
                        {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                      </Select>
                      <Input type="date" value={it.dueDate || ''} onChange={e => patch(it.id, { dueDate: e.target.value })} style={{ width: 138, ...mini }} />
                      <Input value={it.monthlyRange || ''} onChange={e => patch(it.id, { monthlyRange: e.target.value })} placeholder="volume" style={{ width: 120, ...mini }} />
                      {(it.perDay || it.cycleKey === 'stories') && <Input value={it.perDay || ''} onChange={e => patch(it.id, { perDay: e.target.value })} placeholder="cadence" style={{ width: 96, ...mini }} />}
                    </div>
                  </div>
                ) : (
                  <div key={it.id} style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: rowDot(it), flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{it.type}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{[clientName(it.clientId), it.monthlyRange, it.perDay].filter(Boolean).join(' · ')}</div>
                    </div>
                    <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'lining-nums tabular-nums', fontSize: 12.5, color: it.dueDate && it.dueDate < today() && !DONE(it.status) ? 'var(--red)' : 'var(--text3)', whiteSpace: 'nowrap' }}>{it.dueDate ? fmtDate(it.dueDate) : '—'}</span>
                    <StatusCell it={it} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Awaiting client content — open items carried until the client sends what's needed */}
      {isAdmin && awaiting.length > 0 && (
        <Card>
          <SectionHeader title="Awaiting client content" count={awaiting.length} />
          <div>
            {awaiting.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 22px', borderBottom: i < awaiting.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <AlertCircle size={15} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{a.label}{a.note ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> — {a.note}</span> : null}</div>
                </div>
                <Badge label={a.where} color="gray" />
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
